import Link from "next/link";

interface ReviewsTrustWidgetProps {
	href?: string;
	className?: string;
}

const distribution = [
	{ stars: 5, pct: 89 },
	{ stars: 4, pct: 8 },
	{ stars: 3, pct: 2 },
	{ stars: 2, pct: 0.5 },
	{ stars: 1, pct: 0.5 },
];

/**
 * Trustpilot-style review widget — used on homepage (next to Tirzepatide) and /peptides bottom.
 * Self-contained, no client deps. Renders a clean rating card with green Trustpilot-style stars.
 */
export function ReviewsTrustWidget({ href = "#testimonials", className = "" }: ReviewsTrustWidgetProps) {
	return (
		<div
			className={`relative flex h-full flex-col overflow-hidden rounded-md border border-border bg-white transition-all hover:shadow-md ${className}`}
		>
			{/* Top — Score + Trustpilot-style Star-Row */}
			<div className="flex flex-col items-start gap-1.5 p-3 pb-2">
				<div className="font-display text-[2rem] font-bold leading-none tracking-[-0.02em] text-foreground">
					4,7
				</div>
				<div className="flex items-center gap-0.5">
					{Array.from({ length: 5 }).map((_, i) => (
						<span key={i} className="flex h-4 w-4 items-center justify-center rounded-[2px] bg-success">
							<svg viewBox="0 0 20 20" className="h-2.5 w-2.5 fill-white" aria-hidden>
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
						</span>
					))}
				</div>
				<div className="font-display text-[0.8rem] font-semibold text-success">Excellent</div>
				<div className="text-[0.62rem] leading-tight text-muted-foreground">
					Basierend auf <span className="font-semibold text-foreground">287</span> Bewertungen
				</div>
			</div>

			{/* Distribution Bars */}
			<div className="border-t border-border px-3 py-2.5">
				<div className="space-y-1">
					{distribution.map((d) => (
						<div
							key={d.stars}
							className="grid grid-cols-[14px_1fr_24px] items-center gap-1.5 text-[0.55rem] text-muted-foreground"
						>
							<span className="font-medium tabular-nums">{d.stars}★</span>
							<div className="relative h-1.5 overflow-hidden rounded-sm bg-muted">
								<div className="h-full rounded-sm bg-success" style={{ width: `${d.pct}%` }} />
							</div>
							<span className="text-right font-medium tabular-nums text-foreground">{d.pct}%</span>
						</div>
					))}
				</div>
			</div>

			{/* Verifiziert-Badge + "Alle ansehen" Link */}
			<Link
				href={href}
				className="bg-success/[0.06] hover:bg-success/15 group flex items-center justify-between gap-2 border-t border-border px-3 py-2 transition-colors"
			>
				<div className="flex items-center gap-1.5">
					<svg viewBox="0 0 20 20" className="h-3 w-3 fill-success" aria-hidden>
						<path d="M10 0a10 10 0 1 0 10 10A10 10 0 0 0 10 0Zm-1 14.5L4.5 10l1.4-1.4L9 11.7l5.1-5.2 1.4 1.5Z" />
					</svg>
					<span className="text-[0.55rem] font-semibold uppercase tracking-wide text-success">
						Verifiziert
					</span>
				</div>
				<span
					aria-hidden
					className="group-hover:translate-x-0.5 text-[0.6rem] font-semibold text-foreground transition-transform"
				>
					Alle ansehen →
				</span>
			</Link>

			{/* Bewertung abgeben — Login-gated */}
			<Link
				href="/login?next=/account/reviews/new"
				className="hover:bg-success/90 group flex items-center justify-between gap-2 border-t border-success bg-success px-3 py-2.5 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-success-foreground transition-colors"
			>
				<span className="flex items-center gap-1.5">
					<svg viewBox="0 0 20 20" className="h-3.5 w-3.5 fill-current" aria-hidden>
						<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
					</svg>
					Bewertung abgeben
				</span>
				<span aria-hidden className="group-hover:translate-x-0.5 text-[0.62rem] transition-transform">
					→
				</span>
			</Link>
			<div className="bg-success/[0.04] border-t border-border px-3 py-1.5">
				<p className="text-center text-[0.5rem] leading-tight text-muted-foreground">
					Nur für verifizierte Kunden — kurzes Konto erforderlich
				</p>
			</div>
		</div>
	);
}

/**
 * Bewertung-abgeben CTA — separate Card unter dem Widget.
 * Routet zu /login (oder Signup wenn neu) — Bewertungen sind nur für Kunden mit Account.
 */
export function WriteReviewCTA({ className = "" }: { className?: string }) {
	return (
		<Link
			href="/login?next=/account/reviews/new"
			className={`hover:bg-success/90 group inline-flex w-full items-center justify-between gap-2 rounded-md border border-success bg-success px-3 py-2.5 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-success-foreground shadow-sm transition-all ${className}`}
		>
			<span className="flex items-center gap-1.5">
				<svg viewBox="0 0 20 20" className="h-3.5 w-3.5 fill-current" aria-hidden>
					<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
				</svg>
				Bewertung abgeben
			</span>
			<span aria-hidden className="group-hover:translate-x-0.5 text-[0.62rem] transition-transform">
				→
			</span>
		</Link>
	);
}
